import type {FC} from 'react';
import {useQuery, keepPreviousData} from '@tanstack/react-query';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import type {GridSortModel} from '@mui/x-data-grid';
import {useNavigate} from 'react-router-dom';
import {Box} from '@wellstone-solutions/web';
import {Roles} from '@wellstone-solutions/common';
import type {MembersListRoleItem, Member} from 'api/v2';
import {membersQueryOptions} from 'queries';
import {useMembersCounts} from 'modules/member/queries/useMembersCounts';
import {
  RowCount,
  PaginatedListGrid,
  usePaginationParams,
} from 'modules/datagrid';
import {UnassignedEmptyState} from 'components/UnassignedEmptyState';
import {useStaffListColumns} from './useStaffListColumns';

const UNASSIGNED_STAFF_ROLES: MembersListRoleItem[] = [
  Roles.PEER,
  Roles.COUNSELOR,
  Roles.ADMIN,
];

export const UnassignedStaffTab: FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const defaultSortModel: GridSortModel = [{field: 'name', sort: 'asc'}];

  const {page, setPage, pageSize, setPageSize, ordering, setOrdering} =
    usePaginationParams({defaultSortModel});

  const countsQuery = useMembersCounts({role: UNASSIGNED_STAFF_ROLES});
  const unassignedCount = countsQuery.data?.unassigned ?? 0;

  const query = useQuery({
    ...membersQueryOptions.list({
      unassigned: true,
      role: UNASSIGNED_STAFF_ROLES,
      page,
      pageSize,
      ordering,
    }),
    placeholderData: keepPreviousData,
  });

  const columns = useStaffListColumns({isMobile});

  const handleRowClick = ({row}: {row: Member}) => {
    if (globalThis.getSelection()?.type === 'Range') {
      return;
    }

    navigate(`/staff/${row.id}`);
  };

  if (unassignedCount === 0) {
    return <UnassignedEmptyState />;
  }

  return (
    <Box>
      <RowCount
        isLoading={query.isFetching}
        label="unassigned staff"
        count={query.data?.count || 0}
        pluralize={false}
      />

      <PaginatedListGrid
        testId="unassigned-staff-datagrid"
        columns={columns}
        rows={query.data?.results || []}
        rowCount={query.data?.count || 0}
        isLoading={query.isFetching}
        page={page}
        pageSize={pageSize}
        ordering={ordering}
        isMobile={isMobile}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onSortModelChange={setOrdering}
        onRowClick={handleRowClick}
      />
    </Box>
  );
};
